import { useMemo, useState, useEffect } from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, LabelList, ResponsiveContainer } from 'recharts';
import {
  monthsInSeasonWindow,
  mergeYearData,
  buildYearRange,
  yoyDeltaPct,
  MONTH_KEYS,
} from './multi-year-analytics.js';
import { curSym, formatMoney } from './utils.js';
import { CL, FONT, panelStyle } from './theme.js';
import { PanelTitle } from './analytics-ui.jsx';
import MultiYearBackfillModal from './MultiYearBackfillModal.jsx';

// ─── persistence + display helpers ───────────────────────────────────────────

function hiddenYearsKey(propertyId) {
  return `analytics:multiYear:hiddenYears:${propertyId}`;
}

function loadHiddenYears(propertyId) {
  if (!propertyId) return new Set();
  try {
    const raw = localStorage.getItem(hiddenYearsKey(propertyId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter(n => Number.isInteger(n)));
  } catch {
    return new Set();
  }
}

function saveHiddenYears(propertyId, set) {
  if (!propertyId) return; // never write an ...:undefined key (multi-tenant safety)
  try {
    localStorage.setItem(hiddenYearsKey(propertyId), JSON.stringify([...set]));
  } catch {
    /* localStorage unavailable or over quota — keep in-memory only */
  }
}

// Distinct years that would render a non-empty column: a confirmed booking whose
// check-in falls inside that year's season window, or a past_seasons row.
function computeDataYears(bookings, pastSeasons, seasonWindow) {
  const years = new Set(pastSeasons.map(p => p.year));
  for (const b of bookings) {
    if (b.status !== 'confirmed' || !b.check_in) continue;
    const md = b.check_in.slice(5); // 'MM-DD'
    if (md < seasonWindow.startMd || md > seasonWindow.endMd) continue;
    years.add(Number(b.check_in.slice(0, 4)));
  }
  return [...years];
}

// '(+5%)' / '(−5%)' — whole percent, explicit sign, U+2212 minus for negatives.
function fmtYoy(pct) {
  const rounded = Math.round(pct);
  const sign = rounded >= 0 ? '+' : '−';
  return `(${sign}${Math.abs(rounded)}%)`;
}

// A month is "complete" once fully elapsed: its last calendar day < today,
// i.e. today is on/after the first day of the following month.
function monthComplete(year, monthKey, today) {
  const mIdx = MONTH_KEYS.indexOf(monthKey);
  if (mIdx < 0) return false;
  const nextYear  = mIdx === 11 ? year + 1 : year;
  const nextMonth = mIdx === 11 ? 1 : mIdx + 2; // 1-based
  const firstOfNext = `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
  return today >= firstOfNext;
}

// ─── MultiYearView ────────────────────────────────────────────────────────────

export default function MultiYearView({
  property,
  bookings,
  pastSeasons,
  seasons,
  today,
  t,
  lang,
  onBackfillSaved,
}) {
  const cur = curSym(property.currency);
  const currentYear = Number(seasons.thisSeason.start.slice(0, 4));
  const seasonWindow = useMemo(() => ({
    startMd: property.season_start_md || '04-01',
    endMd:   property.season_end_md   || '10-31',
  }), [property.season_start_md, property.season_end_md]);

  const [editYear, setEditYear] = useState(null);          // null | number — modal controller
  const [extraEarliest, setExtraEarliest] = useState(null); // number | null
  const [hiddenYears, setHiddenYears] = useState(() => loadHiddenYears(property.id));
  // Reload the per-property hidden set when the property changes — render-time
  // reset, React's recommended alternative to a setState-in-effect.
  const [loadedFor, setLoadedFor] = useState(property.id);
  if (loadedFor !== property.id) {
    setLoadedFor(property.id);
    setHiddenYears(loadHiddenYears(property.id));
  }

  useEffect(() => { saveHiddenYears(property.id, hiddenYears); }, [property.id, hiddenYears]);

  const pastSeasonsByYear = useMemo(() => {
    const m = new Map();
    for (const ps of pastSeasons) m.set(ps.year, ps);
    return m;
  }, [pastSeasons]);

  const dataYears = useMemo(
    () => computeDataYears(bookings, pastSeasons, seasonWindow),
    [bookings, pastSeasons, seasonWindow],
  );

  const allYears = useMemo(
    () => buildYearRange({ currentYear, dataYears, extraEarliestYear: extraEarliest }),
    [currentYear, dataYears, extraEarliest],
  );

  const merged = useMemo(() => allYears.map(year => {
    const yearStart = `${year}-${seasonWindow.startMd}`;
    const yearEnd   = `${year}-${seasonWindow.endMd}`;
    const yearBookings = bookings.filter(b =>
      b.check_in >= yearStart && b.check_in <= yearEnd
    );
    return mergeYearData({
      year,
      bookings: yearBookings,
      pastSeason: pastSeasonsByYear.get(year) || null,
      seasonWindow,
      isInProgress: year === currentYear && today < yearEnd,
    });
  }), [allYears, bookings, pastSeasonsByYear, seasonWindow, currentYear, today]);

  const mergedByYear  = useMemo(() => new Map(merged.map(m => [m.year, m])), [merged]);
  const visibleYears  = useMemo(() => allYears.filter(y => !hiddenYears.has(y)), [allYears, hiddenYears]);
  const visibleMerged = useMemo(
    () => visibleYears.map(y => mergedByYear.get(y)).filter(Boolean),
    [visibleYears, mergedByYear],
  );

  const hideYear = (y) => setHiddenYears(prev => { const n = new Set(prev); n.add(y); return n; });
  const showYear = (y) => setHiddenYears(prev => { const n = new Set(prev); n.delete(y); return n; });
  const addEarlier = () => {
    const next = Math.min(...allYears) - 1;
    setExtraEarliest(next);
    setHiddenYears(prev => {
      if (!prev.has(next)) return prev;
      const n = new Set(prev); n.delete(next); return n;
    });
    setEditYear(next);
  };

  return (
    <div>
      <YearControlRow
        visibleYears={visibleYears}
        restorableYears={allYears.filter(y => hiddenYears.has(y))}
        t={t}
        onHide={hideYear}
        onShow={showYear}
        onAddEarlier={addEarlier}
      />
      <YearlyBars merged={visibleMerged} mergedByYear={mergedByYear} t={t} cur={cur} onAddYear={setEditYear} />
      <MonthYearGrid
        merged={visibleMerged}
        mergedByYear={mergedByYear}
        seasonWindow={seasonWindow}
        today={today}
        t={t}
        cur={cur}
        onAddCell={(year) => setEditYear(year)}
      />
      <SourceMixPerYear merged={visibleMerged} t={t} cur={cur} />
      {editYear != null && (
        <MultiYearBackfillModal
          year={editYear}
          property={property}
          existingPastSeason={pastSeasonsByYear.get(editYear) || null}
          realBookingsByMonth={realBookingsByMonth(bookings, editYear, seasonWindow)}
          seasonWindow={seasonWindow}
          t={t}
          lang={lang}
          onClose={() => setEditYear(null)}
          onSaved={async () => { await onBackfillSaved?.(); setEditYear(null); }}
        />
      )}
    </div>
  );
}

function realBookingsByMonth(bookings, year, seasonWindow) {
  const out = {};
  const yearStart = `${year}-${seasonWindow.startMd}`;
  const yearEnd   = `${year}-${seasonWindow.endMd}`;
  for (const b of bookings) {
    if (b.status !== 'confirmed') continue;
    if (b.check_in < yearStart || b.check_in > yearEnd) continue;
    const mIdx = Number(b.check_in.slice(5, 7)) - 1;
    const mKey = MONTH_KEYS[mIdx];
    const ci = new Date(b.check_in + 'T12:00:00Z');
    const co = new Date(b.check_out + 'T12:00:00Z');
    const nights = Math.round((co - ci) / 86400000);
    if (nights <= 0) continue;
    if (!out[mKey]) out[mKey] = { revenue: 0, nights: 0 };
    out[mKey].revenue += Number(b.price) || 0;
    out[mKey].nights  += nights;
  }
  return out;
}

// ─── YearControlRow ───────────────────────────────────────────────────────────

function YearControlRow({ visibleYears, restorableYears, t, onHide, onShow, onAddEarlier }) {
  const pill = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: CL.cream, border: `1px solid ${CL.goldL}`, color: CL.bark,
    borderRadius: 999, padding: '4px 10px', fontSize: 12, fontFamily: FONT.body,
  };
  const xBtn = {
    background: 'none', border: 'none', color: CL.stone, cursor: 'pointer',
    fontSize: 13, lineHeight: 1, padding: 0,
  };
  const restore = {
    background: 'none', border: `1px dashed ${CL.stone}`, color: CL.stone,
    borderRadius: 999, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontFamily: FONT.body,
  };
  const addBtn = {
    background: 'none', border: 'none', color: CL.stone, cursor: 'pointer',
    fontSize: 12, fontFamily: FONT.body, padding: '4px 6px',
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      {visibleYears.length === 0 && (
        <span style={{ fontSize: 12, color: CL.stone, fontFamily: FONT.body }}>
          {t.analytics.multiYear.allYearsHidden}
        </span>
      )}
      {visibleYears.map(y => (
        <span key={y} style={pill}>
          {y}
          <button
            type="button"
            onClick={() => onHide(y)}
            aria-label={t.analytics.multiYear.hideYear.replace('{year}', y)}
            style={xBtn}
          >×</button>
        </span>
      ))}
      {restorableYears.map(y => (
        <button
          key={y}
          type="button"
          onClick={() => onShow(y)}
          aria-label={t.analytics.multiYear.showYear.replace('{year}', y)}
          style={restore}
        >+ {y}</button>
      ))}
      <button type="button" onClick={onAddEarlier} style={addBtn}>
        {t.analytics.multiYear.addEarlier}
      </button>
    </div>
  );
}

// ─── YearlyBars ─────────────────────────────────────────────────────────────

function YearlyBars({ merged, mergedByYear, t, cur, onAddYear }) {
  const data = merged.map(y => ({
    yearLabel: String(y.year),
    year: y.year,
    revenue: y.totalRevenue,
    isInProgress: y.isInProgress,
    isPlaceholder: !y.hasRealBookings && !y.hasBackfill,
  }));

  return (
    <section style={panelStyle}>
      <PanelTitle>{t.analytics.multiYear.yearlyRevenue}</PanelTitle>
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 44, right: 16, left: 0, bottom: 8 }}>
            <XAxis dataKey="yearLabel" tick={{ fontSize: 11, fill: CL.bark }} />
            <YAxis tick={{ fontSize: 11, fill: CL.stone }} tickFormatter={v => formatMoney(v, cur)} />
            <Tooltip formatter={(v) => formatMoney(v, cur)} cursor={{ fill: CL.sand }} />
            <Bar dataKey="revenue" onClick={(d) => { if (d.isPlaceholder) onAddYear(d.year); }} cursor="pointer">
              {data.map((d, i) => (
                <Cell
                  key={i}
                  fill={d.isPlaceholder ? 'transparent' : CL.bark}
                  stroke={d.isPlaceholder ? CL.stone : 'none'}
                  strokeDasharray={d.isPlaceholder ? '4 4' : '0'}
                  fillOpacity={d.isInProgress ? 0.5 : 1}
                />
              ))}
              <LabelList
                dataKey="revenue"
                position="top"
                content={(props) => {
                  const { x, y, width, index } = props;
                  const d = data[index];
                  if (!d || d.isPlaceholder || !(d.revenue > 0)) return null;
                  const prior = mergedByYear.get(d.year - 1);
                  const pct = d.isInProgress
                    ? null
                    : yoyDeltaPct(d.revenue, prior ? prior.totalRevenue : null);
                  const cx = x + width / 2;
                  return (
                    <text x={cx} y={y - 22} textAnchor="middle" fontFamily={FONT.display}>
                      <tspan x={cx} dy="0" fontSize="13" fontWeight="700" fill={CL.bark}
                             style={{ fontVariantNumeric: 'lining-nums tabular-nums' }}>
                        {formatMoney(d.revenue, cur)}
                      </tspan>
                      {pct != null && (
                        <tspan x={cx} dy="13" fontSize="10" fontWeight="600" fontFamily={FONT.body}
                               fill={pct >= 0 ? CL.olive : CL.terra}>
                          {fmtYoy(pct)}
                        </tspan>
                      )}
                    </text>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

// ─── MonthYearGrid ──────────────────────────────────────────────────────────

function MonthYearGrid({ merged, mergedByYear, seasonWindow, today, t, cur, onAddCell }) {
  const months = monthsInSeasonWindow(seasonWindow.startMd, seasonWindow.endMd);
  let maxRev = 0;
  for (const y of merged) {
    for (const m of months) {
      const rev = y.monthly[m]?.revenue ?? 0;
      if (rev > maxRev) maxRev = rev;
    }
  }
  const cellBg = (rev) => {
    if (rev <= 0 || maxRev === 0) return 'transparent';
    const ratio = rev / maxRev;
    const lerp = (a, b) => Math.round(a + (b - a) * ratio);
    const r = lerp(0xE8, 0xC9);
    const g = lerp(0xD5, 0xA9);
    const b = lerp(0xB0, 0x6E);
    return `rgb(${r},${g},${b})`;
  };

  return (
    <section style={panelStyle}>
      <PanelTitle>{t.analytics.multiYear.monthYearGrid}</PanelTitle>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 4, fontSize: 12, color: CL.bark }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 8px', color: CL.stone, fontWeight: 500 }}></th>
              {merged.map(y => (
                <th key={y.year} style={{ textAlign: 'center', padding: '4px 8px', color: CL.stone, fontWeight: 500 }}>
                  {y.year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {months.map(m => (
              <tr key={m}>
                <td style={{ textAlign: 'left', padding: '4px 8px', color: CL.bark, fontWeight: 500, textTransform: 'capitalize' }}>
                  {m}
                </td>
                {merged.map(y => {
                  const cell = y.monthly[m];
                  const rev = cell?.revenue ?? 0;
                  const isEmpty = !cell;
                  let pct = null;
                  if (cell) {
                    const priorRev = mergedByYear.get(y.year - 1)?.monthly?.[m]?.revenue;
                    const complete = cell.fromBackfill || monthComplete(y.year, m, today);
                    if (complete && priorRev > 0) pct = yoyDeltaPct(rev, priorRev);
                  }
                  return (
                    <td
                      key={y.year}
                      onClick={() => isEmpty ? onAddCell(y.year, m) : null}
                      style={{
                        position: 'relative',
                        textAlign: 'center',
                        padding: '6px 10px',
                        minWidth: 72,
                        background: isEmpty ? 'transparent' : cellBg(rev),
                        border: isEmpty ? `1px dashed ${CL.sand}` : `1px solid ${CL.sand}`,
                        borderRadius: 4,
                        cursor: isEmpty ? 'pointer' : 'default',
                        color: isEmpty ? CL.stone : CL.bark,
                      }}
                    >
                      {isEmpty ? '+' : formatMoney(rev, cur)}
                      {pct != null && (
                        <div style={{
                          fontSize: 10, fontWeight: 700, marginTop: 2,
                          color: pct >= 0 ? CL.olive : CL.terra,
                          textShadow: '0 0 1px #FFFDF8',
                        }}>
                          {fmtYoy(pct)}
                        </div>
                      )}
                      {cell?.fromBackfill && (
                        <span style={{ position: 'absolute', top: 2, right: 4, fontSize: 8, color: CL.stone }}>●</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: CL.stone, display: 'flex', gap: 16 }}>
        <span>{t.analytics.multiYear.legendBackfill}</span>
        <span>{t.analytics.multiYear.legendReal}</span>
      </div>
    </section>
  );
}

// ─── SourceMixPerYear ───────────────────────────────────────────────────────

function SourceMixPerYear({ merged, t, cur }) {
  const labels = {
    direct:      t.analytics.backfill.sourceDirect,
    booking_com: t.analytics.backfill.sourceBcom,
    other:       t.analytics.backfill.sourceOther,
  };
  const segColor = { direct: CL.olive, booking_com: CL.sea, other: CL.goldL };
  const order = ['direct', 'booking_com', 'other'];

  return (
    <section style={panelStyle}>
      <PanelTitle>{t.analytics.multiYear.sourceMixPerYear}</PanelTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {merged.map(y => {
          const mix = y.sourceMix;
          const total = mix ? (mix.direct + mix.booking_com + mix.other) : 0;
          return (
            <div key={y.year} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ width: 40, fontSize: 12, color: CL.bark, fontWeight: 600, paddingTop: 2 }}>
                {y.year}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {(!mix || total <= 0) ? (
                  <>
                    <div style={{ height: 20, background: CL.stone, opacity: 0.2, borderRadius: 4 }} />
                    <div style={{ fontSize: 10, color: CL.stone, marginTop: 4 }}>
                      {t.analytics.multiYear.sourceUnknown}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', height: 20, borderRadius: 4, overflow: 'hidden' }}>
                      {order.map(k => {
                        const segPct = (mix[k] / total) * 100;
                        if (segPct <= 0) return null;
                        return (
                          <div
                            key={k}
                            title={`${labels[k]}: ${formatMoney(mix[k], cur)}`}
                            style={{
                              width: `${segPct}%`, background: segColor[k],
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 9, fontWeight: 600, color: k === 'other' ? CL.bark : '#fff',
                            }}
                          >
                            {segPct >= 12 ? `${Math.round(segPct)}%` : ''}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 11, color: CL.stone, marginTop: 4 }}>
                      {order.filter(k => mix[k] > 0)
                            .map(k => `${labels[k]} ${Math.round((mix[k] / total) * 100)}%`)
                            .join(' · ')}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: CL.stone, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {order.map(k => (
          <span key={k}>
            <span style={{ display: 'inline-block', width: 10, height: 10, background: segColor[k], marginRight: 4 }} />
            {labels[k]}
          </span>
        ))}
      </div>
    </section>
  );
}
